import { canViewSessionReplayForApp } from '@/vuex/modules/auth/auth.helpers';
import { saveInsightsSegment, triggerMLPipeline } from '@/insights/utils/utils';
import { visitorList } from './segment-eligible-visitor-list.aggregation';
import { validateDelayedAnalyticsWithSegmentFilters } from '@/segments/delayed-segment-csv.utils';
import { runAggregation } from '@/utils/aggregation';
import {
    isSupportedGuideFilter,
    segmentRuleNotSupportedText,
    getEnabledDefaultSegments,
    isEmailSegmentGuideEligible,
    getDefaultSegment,
    isAdoptSegment,
} from '@utils/segments.js';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isString from 'lodash/isString';
import { findIdentified } from '@pendo/agg-connect';
import {
    fetchSegments,
    fetchSegment,
    createSegment,
    deleteSegment,
    updateSegment,
    filtersHaveRulesOfKind,
    sendSaveSegmentTrackEvents,
} from './segment.helpers';
import { getPipelineProps } from '@/segments/segment';
import { updateVerificationState } from '@/common/verification-status/verification-status.utils';
import { i18n_t as $t } from '@utils/i18n';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { hasNoAccountRules, isDefaultSegment } from '@/utils/segments';
import { getGuideTargetingSegment } from '@/guides/helpers/common.util';

let loadAllPromise;
const editorDefaults = {
    confirmDelete: false,
    saved: false,
    segment: null,
    visitorCount: null,
};

const customSegmentDefaults = {
    id: 'custom',
    name: 'Custom Segment',
    definition: {
        filters: [],
    },
    pipeline: [],
};

const apiSegmentFields = { username: 'API', first: 'API', last: 'API' };

const activeSegmentDefaults = {
    segment: {},
    truncatedVisitorList: [],
    eligibleVisitorCount: undefined,
    fetching: false,
};

const segments = {
    namespaced: true,
    state: {
        map: {},
        error: {},
        editor: Object.assign({}, editorDefaults),
        customSegment: Object.assign({}, customSegmentDefaults),
        activeSegmentDetails: Object.assign({}, activeSegmentDefaults),
        fetching: false,
        isDeleteModalOpen: false,
        defaultSegments: [],
        segmentDependents: {},
        segmentsToDelete: [],
        selectedSegments: [],
        isInsightsSegmentEligible: false,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, segment }) {
            state.map[id] = segment;
        },
        deleteMapKey (state, { id }) {
            delete state.map[id];
        },
        setError (state, { segmentId, error }) {
            state.error[segmentId] = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setDefaultSegments (state, { defaultSegments }) {
            state.defaultSegments = defaultSegments;
        },
        setCustomSegmentFilters (state, { filters }) {
            state.customSegment.definition.filters = filters;
        },
        setCustomSegmentPipeline (state, { pipeline }) {
            state.customSegment.pipeline = pipeline;
        },
        setCustomCompoundSegmentPipelines (state, { pipelines, compound }) {
            state.customSegment.pipelines = pipelines;
            state.customSegment.compound = compound;
        },
        setCustomSegmentAdvancedOptions (state, { advancedOptions }) {
            state.customSegment.definition.advancedOptions = advancedOptions;
        },
        setEditorWithSegmentId (state, { segmentId }) {
            state.editor.segment = cloneDeep(state.map[segmentId]);
        },
        setEditorWithSegment (state, { segment }) {
            state.editor.segment = cloneDeep(segment);
        },
        cancelEditSegment (state) {
            Object.assign(state.editor, editorDefaults);
        },
        setActiveSegmentFetching (state, { fetching }) {
            state.activeSegmentDetails.fetching = fetching;
        },
        setActiveSegmentVisitorList (state, { visitorList }) {
            state.activeSegmentDetails.truncatedVisitorList = visitorList;
        },
        setActiveSegmentVisitorCount (state, { visitorCount }) {
            state.activeSegmentDetails.eligibleVisitorCount = visitorCount;
        },
        setActiveSegment (state, { segment }) {
            state.activeSegmentDetails.segment = segment;
        },
        clearActiveSegmentDetails (state) {
            state.activeSegmentDetails = Object.assign({}, activeSegmentDefaults);
        },
        setSegmentDependents (state, segmentDependents) {
            state.segmentDependents = segmentDependents;
        },
        setSegmentDependentsAtKey (state, { id, segmentDependents }) {
            state.segmentDependents[id] = segmentDependents;
        },
        clearSegmentDependents (state) {
            state.segmentDependents = {};
        },
        setSegmentsToDelete (state, segments) {
            state.segmentsToDelete = cloneDeep(segments);
        },
        setSelectedSegments (state, segments) {
            state.selectedSegments = cloneDeep(segments);
        },
        removeSelectedSegmentAtIndex (state, index) {
            state.selectedSegments.splice(index, 1);
        },
        setIsInsightsSegmentEligible (state, isEligible) {
            state.isInsightsSegmentEligible = isEligible;
        },
    },
    actions: {
        async loadAll ({ commit, dispatch }, { reFetch = false, excludeBlacklist = false } = {}) {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            commit('setFetching', { fetching: true });

            loadAllPromise = Promise.all([
                fetchSegments(),
                getEnabledDefaultSegments(excludeBlacklist),
                dispatch('insights/loadAllFocusGroups', {}, { root: true }), // in loadAllFocusGroups we check if the feature is enabled and only if yes, we load them
            ]);
            try {
                const [segments, defaultSegments, focusGroups = []] = await loadAllPromise;
                for (const segment of segments) {
                    if (segment.createdByApi) {
                        Object.assign(segment.lastUpdatedByUser, apiSegmentFields);
                        Object.assign(segment.createdByUser, apiSegmentFields);
                        Object.assign(segment.definition, {
                            apiManagedList: true,
                            filters: [
                                {
                                    group: 'auto',
                                    kind: 'visitor',
                                    field: 'visitor.auto.id',
                                    id: 'visitor_auto_id',
                                    type: 'visitor.auto.id',
                                    name: 'Visitor ID',
                                    schema: 'string',
                                    operator: 'listContains',
                                    $valid: true,
                                    analyticsDelayedAtTimestamp: null,
                                },
                            ],
                        });
                    }
                }

                const focusGroupsWithoutEveryone = focusGroups.filter((focusGroup) => focusGroup.id !== '0');

                commit('setFetching', { fetching: false });
                commit('setMap', { map: keyBy([...segments, ...focusGroupsWithoutEveryone], 'id') });
                commit('setDefaultSegments', { defaultSegments });
            } catch (error) {
                commit('setFetching', { fetching: false });
                throw error;
            }
        },
        async loadOne ({ commit, state }, { reFetch = false, id }) {
            if (!reFetch) {
                await loadAllPromise;
            }

            if (state.map[id] && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });
            // commit('setError', { segmentId: id, error: { message: undefined } }); // causing the error banner to show after loading one

            try {
                const segment = await fetchSegment(id);
                if (segment.createdByApi) {
                    Object.assign(segment.lastUpdatedByUser, apiSegmentFields);
                    Object.assign(segment.createdByUser, apiSegmentFields);
                }
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', { id: segment.id, segment });
            } catch (error) {
                commit('setFetching', { fetching: false });
                if (error && error.message !== 'cancelled') {
                    commit('setError', { segmentId: id, error });
                }
                throw error;
            }
        },
        async saveSegment ({ dispatch, commit, state, getters }, { segment, insightsAppId = undefined, location = '' }) {
            if (state.editor.segment.id === 'custom') {
                commit('setCustomSegmentFilters', {
                    filters: segment.definition.filters,
                });
                commit('setCustomSegmentPipeline', {
                    pipeline: segment.pipeline,
                });

                if (segment.compound) {
                    commit('setCustomCompoundSegmentPipelines', {
                        compound: segment.compound,
                        pipelines: segment.pipelines,
                    });
                }

                const selectedVisitorType = get(findIdentified(segment.pipeline), 'identified', null);

                commit('setCustomSegmentAdvancedOptions', {
                    advancedOptions: {
                        ...get(segment, 'definition.advancedOptions'),
                        selectedVisitorType,
                    },
                });
                commit('cancelEditSegment');

                return state.customSegment;
            }

            try {
                const action = segment.id ? 'update' : 'create';
                let savedSegment = {};
                if (segment.isFocusGroup && insightsAppId) {
                    savedSegment = await saveInsightsSegment({ appId: insightsAppId, segment });
                    if (state.isInsightsSegmentEligible) {
                        triggerMLPipeline({ appId: insightsAppId, insightsSegmentId: savedSegment.id });
                    }
                } else {
                    const saveFn = action === 'update' ? updateSegment : createSegment;
                    savedSegment = await saveFn(segment);

                    commit('setMapAtKey', { id: savedSegment.id, segment: savedSegment });

                    const parentSegmentIds = getters.parentSegmentsById(savedSegment.id).map((parent) => parent.id);
                    /* eslint-disable no-await-in-loop */
                    for (const parentSegmentId of parentSegmentIds) {
                        let parentSegment = state.map[parentSegmentId];
                        if (!parentSegment) {
                            await dispatch('loadOne', { id: parentSegmentId, reFetch: true });
                            parentSegment = state.map[parentSegmentId];
                        }
                        try {
                            const updatedPipelineProps = await getPipelineProps(parentSegment);
                            const updatedParent = { ...parentSegment, ...updatedPipelineProps };
                            await dispatch('saveSegment', {
                                segment: updatedParent,
                                insightsAppId,
                                location,
                            });
                        } catch (err) {
                            console.error(
                                `Failed to recompile parent segment ${parentSegment.id} after saving nested segment:`,
                                err,
                            );
                        }
                    }
                    /* eslint-enable no-await-in-loop */

                    if (get(state, 'activeSegmentDetails.segment.id') === savedSegment.id) {
                        dispatch('loadActiveSegment', {
                            segment: savedSegment,
                            reFetchDependents: false,
                        });
                    }

                    sendSaveSegmentTrackEvents(savedSegment, { action, location });
                }
                commit('cancelEditSegment');

                return savedSegment;
            } catch (error) {

                console.error(error);
                commit('setError', { segmentId: segment.id, error });
                throw error;
            }
        },
        async updateSegmentVerificationState ({ commit, dispatch, state }, { id, status, notes }) {
            await updateVerificationState({
                kind: 'segment',
                id,
                state: status,
                notes,
            });

            await dispatch('loadOne', { id, reFetch: true });

            if (get(state, 'activeSegmentDetails.segment.id') === id) {
                dispatch('loadActiveSegment', {
                    segment: state.map[id],
                    reFetchDependents: false,
                });
            }

            if (get(state, 'editor.segment.id') === id) {
                commit('setEditorWithSegmentId', { segmentId: id });
            }
        },
        async simpleSave ({ commit }, { segment }) {
            try {
                const action = segment.id && segment.id !== 'draft0' ? 'update' : 'create';
                const saveFn = action === 'update' ? updateSegment : createSegment;
                const savedSegment = await saveFn(segment);
                commit('setMapAtKey', { id: savedSegment.id, segment: savedSegment });

                sendSaveSegmentTrackEvents(savedSegment, 'create');

                return savedSegment;
            } catch (error) {

                console.error(error);
                commit('setError', { segmentId: segment.id, error });
                throw error;
            }
        },
        setSegmentsToDelete ({ commit, dispatch }, segments) {
            dispatch('loadDependents', { segments, reFetch: false, reCheckDependencies: true });
            commit('setSegmentsToDelete', segments);
        },
        deleteSegment ({ commit, state, getters }, { segmentId } = {}) {
            const segment = state.map[segmentId];
            const selectedSegmentIndex = get(getters.selectedSegmentsMap, segmentId, -1);
            if (selectedSegmentIndex > -1) {
                commit('removeSelectedSegmentAtIndex', selectedSegmentIndex);
            }

            if (segmentId === get(state.activeSegmentDetails.segment, 'id')) {
                commit('clearActiveSegmentDetails');
            }

            commit('deleteMapKey', { id: segmentId });

            return deleteSegment(segment).catch((error) => {
                if (error && error.message !== 'cancelled') {
                    commit('setError', { segmentId: segment.id, error });
                }
                throw error;
            });
        },
        async loadDependents (
            { rootState, dispatch, commit, state, getters, rootGetters },
            { segments, reFetch = true, reCheckDependencies = false },
        ) {
            if (!segments.length) return Promise.resolve();
            const activeSub = rootState.subscription.active.id;
            const dependentsNotLoadedSegments = [];

            segments.forEach((segment) => {
                const { id } = segment;
                if (reFetch || !state.segmentDependents[id] || reCheckDependencies) {
                    commit('setSegmentDependentsAtKey', { id, segmentDependents: Object.freeze({ fetching: true }) });
                    dependentsNotLoadedSegments.push(segment);
                }
            });

            if (!dependentsNotLoadedSegments.length) {
                return Promise.resolve();
            }

            const canUseReplay = rootGetters['subscription/hasSessionReplayEnabled'];
            const canUseEmails = rootGetters['emails/hasEmailStandAlone'];
            const canUseJourneys = rootGetters['journeys/hasJourneysEnabled'];

            const loadPromises = [];
            const modules = [
                { shouldFetch: true, getter: 'guides/list', loadAction: 'guides/loadAll' },
                { shouldFetch: true, getter: 'savedReports/list', loadAction: 'savedReports/loadAll' },
                { shouldFetch: canUseReplay, getter: 'apps/list', loadAction: 'apps/loadAll' },
                { shouldFetch: canUseEmails, getter: 'emails/list', loadAction: 'emails/loadAll' },
                { shouldFetch: canUseJourneys, getter: 'journeys/list', loadAction: 'journeys/loadAllJourneys' },
            ];
            for (const { shouldFetch, getter, loadAction } of modules) {
                if (shouldFetch && (reFetch || !rootGetters[getter].length)) {
                    loadPromises.push(dispatch(loadAction, {}, { root: true }));
                }
            }
            if (loadPromises.length) {
                await Promise.all(loadPromises);
            }

            const guides = rootGetters['guides/list'];
            const reports = rootGetters['savedReports/list'];
            const emails = rootGetters['emails/list'];
            const journeys = rootGetters['journeys/list'];

            let sessionReplayAppSettings;
            if (canUseReplay) {
                const apps = rootGetters['apps/list'];
                sessionReplayAppSettings = await Promise.all(
                    apps
                        .filter(({ id }) => rootGetters['apps/getApplicationFlag'](id, 'enableSessionReplay') && canViewSessionReplayForApp(id))
                        .map(async (app) => {
                            let replaySettings;
                            try {
                                replaySettings = await dispatch(
                                    'sessionReplay/loadReplaySettingsForApp',
                                    { appId: app.id },
                                    { root: true },
                                );
                            } catch (err) {
                                console.error('Unable to fetch replay settings for app: ', err);
                            }

                            return {
                                replaySettings,
                                app,
                            };
                        }),
                );
            }

            dependentsNotLoadedSegments.forEach((segment) => {
                const { id } = segment;

                const dependentCustomSegments = [];
                const dependentGuides = [];
                const dependentEmails = [];
                const dependentJourneys = [];

                const mapGuideToDependent = (guide) => ({
                    ...pick(guide, 'id', 'name', 'state'),
                    routeObj: { name: 'GuideDetails', params: { guideId: guide.id, sub: activeSub } },
                });

                const mapEmailToDependent = (email) => ({
                    ...pick(email, 'id', 'name', 'status'),
                    routeObj: { name: 'EmailDetails', params: { id: email.id, sub: activeSub } },
                });

                const mapJourneyToDependent = (journey) => ({
                    ...pick(journey, 'id', 'name', 'status'),
                    routeObj: { name: 'JourneyDetails', params: { id: journey.id, sub: activeSub } },
                });

                guides.forEach((guide) => {
                    const filters = guide.audienceUiHint?.filters;
                    const segmentInFilters = filters?.some((filter) =>
                        (filter.or ? filter.or.some((rule) => rule.segmentId === id) : filter.segmentId === id),
                    );

                    if (!segmentInFilters) return;

                    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                    if (isCustomNestedSegment) {
                        return dependentCustomSegments.push(mapGuideToDependent(guide));
                    }

                    return dependentGuides.push(mapGuideToDependent(guide));
                });

                emails.forEach((email) => {
                    const { attributes, audienceUIHint = {} } = email;
                    const { filters } = audienceUIHint;

                    if (attributes?.journeyId) return;

                    const segmentInFilters = filters?.some((filter) =>
                        (filter.or ? filter.or.some((rule) => rule.segmentId === id) : filter.segmentId === id),
                    );

                    if (!segmentInFilters) return;

                    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                    if (isCustomNestedSegment) {
                        return dependentCustomSegments.push({ ...mapEmailToDependent(email), type: 'email' });
                    }

                    return dependentEmails.push(mapEmailToDependent(email));
                });

                journeys.forEach((journey) => {
                    const { audienceUiHint = {} } = journey;
                    const { filters } = audienceUiHint;

                    const segmentInFilters = filters?.some((filter) =>
                        (filter.or ? filter.or.some((rule) => rule.segmentId === id) : filter.segmentId === id),
                    );

                    if (!segmentInFilters) return;

                    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                    if (isCustomNestedSegment) {
                        return dependentCustomSegments.push({ ...mapJourneyToDependent(journey), type: 'journey' });
                    }

                    return dependentJourneys.push(mapJourneyToDependent(journey));
                });

                const dependentDraftSegments = [];
                const dependentFullReports = [];

                reports.forEach((report) => {
                    if (report.segmentId === id) {
                        dependentFullReports.push(report);
                    }

                    if (report.segments) {
                        const { segments } = report;

                        segments.forEach((segment) => {
                            if (segment.compound?.includes(id)) {
                                const miniReport = pick(report, 'id', 'name');
                                dependentDraftSegments.push({ report: miniReport, draftSegment: segment });
                            }
                        });

                        if (segments.some((segment) => segment.id === id)) dependentFullReports.push(report);
                    }

                    if (report.segments?.includes((segment) => segment.id === id)) dependentFullReports.push(report);
                });

                const dependentReports = dependentFullReports.map((report) => {
                    const miniReport = pick(report, 'id', 'name');
                    let namedRoute;
                    if (report.definition?.type === 'List') {
                        switch (report.definition.kind.toLowerCase()) {
                            case 'account':
                                namedRoute = 'AccountListReport';
                                break;
                            case 'parentaccount':
                                namedRoute = 'ParentAccountListReport';
                                break;
                            case 'visitor':
                                namedRoute = 'VisitorListReport';
                                break;
                            default:
                                namedRoute = 'NotFound';
                                break;
                        }
                        miniReport.routeObj = {
                            name: namedRoute,
                            params: { reportId: report.id, sub: activeSub },
                        };
                    } else if (report.type === 'Saved Reports') {
                        miniReport.routeObj = {
                            name: 'DataExplorer',
                            params: { id: report.id, sub: activeSub },
                        };
                    } else {
                        switch (report.type?.toLowerCase()) {
                            case 'funnel':
                                namedRoute = 'FunnelDetails';
                                break;
                            case 'path':
                                namedRoute = 'PathDetails';
                                break;
                            case 'retention':
                                namedRoute = 'RetentionDetails';
                                break;
                            default:
                                namedRoute = 'NotFound';
                                break;
                        }
                        miniReport.routeObj = {
                            name: namedRoute,
                            params: { reportId: report.id, sub: activeSub },
                        };
                    }

                    return miniReport;
                });
                const dependentSegments = getters.parentSegmentsById(id);

                // Replay
                const dependentReplayApps = [];

                if (canUseReplay) {
                    const mapAppToDependent = (app) => ({
                        name: app.displayName,
                        id: app.id,
                        routeObj: {
                            name: 'AppAdmin',
                            params: { appId: app.id, sub: activeSub },
                            query: { tab: 'replay' },
                        },
                    });

                    sessionReplayAppSettings.forEach(({ replaySettings, app }) => {
                        const filters = get(replaySettings, 'audienceUiHint.filters', []);

                        if (!filters.length) return;

                        const segmentInFilters = filters.some((filter) =>
                            (filter.or ? filter.or.some((rule) => rule.segmentId === id) : filter.segmentId === id),
                        );

                        if (!segmentInFilters) return;

                        const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                        if (isCustomNestedSegment) {
                            return dependentCustomSegments.push({ ...mapAppToDependent(app), type: 'replay' });
                        }

                        return dependentReplayApps.push(mapAppToDependent(app));
                    });
                }

                commit('setSegmentDependentsAtKey', {
                    id,
                    segmentDependents: Object.freeze({
                        dependentGuides,
                        dependentEmails,
                        dependentJourneys,
                        dependentReports,
                        dependentSegments,
                        dependentDraftSegments,
                        dependentCustomSegments,
                        dependentReplayApps,
                        fetching: false,
                    }),
                });
            }, {});
        },
        clearSegmentDependents ({ commit }) {
            commit('clearSegmentDependents');
        },
        updateCustomSegment ({ commit }, { filters, pipeline, pipelines, compound, advancedOptions = {} }) {
            commit('setCustomSegmentFilters', { filters });
            commit('setCustomSegmentPipeline', { pipeline });

            if (compound) {
                commit('setCustomCompoundSegmentPipelines', {
                    compound,
                    pipelines,
                });
            }

            const selectedVisitorType = getSelectedVisitorType(pipeline);

            commit('setCustomSegmentAdvancedOptions', {
                advancedOptions: {
                    ...advancedOptions,
                    selectedVisitorType,
                },
            });
        },
        clearCustomSegment ({ dispatch }) {
            dispatch('updateCustomSegment', { pipeline: [], filters: [] });
        },
        createSegment ({ dispatch }) {
            dispatch('cloneSegment', { createNew: true });
        },
        createInsightsSegment ({ dispatch }) {
            dispatch('cloneSegment', { createNew: true, isInsightsSegment: true });
        },
        createSegmentWithDefinition (
            { commit },
            { definition = {}, name = 'Unnamed', shared = true, description = '' },
        ) {
            if (!definition.filters) {
                definition.filters = [];
            }
            if (!definition.advancedOptions) {
                definition.advancedOptions = { selectedVisitorType: null };
            }
            const newSegment = {
                definition,
                name,
                shared,
                description,
                flagName: '',
                kind: 'Segment',
            };

            commit('setEditorWithSegment', { segment: newSegment });
        },
        async cloneSegment (
            { state, commit, dispatch, rootGetters },
            { segmentId = null, createNew = false, readOnly = false, isInsightsSegment = false },
        ) {
            let segmentToClone;
            if (createNew) {
                segmentToClone = {
                    definition: {
                        filters: [],
                        advancedOptions: {
                            selectedVisitorType: null,
                        },
                    },
                    ...(isInsightsSegment && { isFocusGroup: true }),
                    kind: 'Segment',
                    shared: !rootGetters['auth/isReadOnly'],
                    description: '',
                };
            } else {
                await dispatch('loadOne', { id: segmentId, reFetch: true });
                segmentToClone = state.map[segmentId];
            }
            const newSegment = cloneDeep(
                pick(segmentToClone, 'definition', 'shared', 'description', 'kind', 'isFocusGroup'),
            );
            newSegment.name = createNew ? '' : `Copy of ${segmentToClone.name}`;
            if (readOnly) {
                newSegment.shared = false;
            }
            newSegment.flagName = '';
            commit('setEditorWithSegment', { segment: newSegment });
        },
        async editSegment ({ commit, dispatch }, { segmentId }) {
            await dispatch('loadOne', { id: segmentId, reFetch: true });
            commit('setEditorWithSegmentId', { segmentId });
        },
        editCustomSegment ({ state, commit }) {
            commit('setEditorWithSegment', { segment: state.customSegment });
        },
        async loadActiveSegment ({ commit, dispatch }, { segment, reFetchAnalytics = true }) {
            if (!segment) return;
            commit('setError', { segmentId: segment.id, error: undefined });

            if (reFetchAnalytics) {
                commit('clearActiveSegmentDetails');
                commit('setActiveSegmentFetching', { fetching: true });
            }

            const activeSegment = pick(
                segment,
                'name',
                'id',
                'definition',
                'pipeline',
                'pipelines',
                'compound',
                'createdByUser',
                'lastUpdatedByUser',
                'shared',
                'description',
                'createdByApi',
                'verification',
                'analyticsDelayed',
                'includeForOEM',
            );

            activeSegment.createdBy = segment.createdByUser.username;
            if (reFetchAnalytics) {
                activeSegment.analyticsDelayed = await validateDelayedAnalyticsWithSegmentFilters(
                    activeSegment?.definition?.filters,
                );
            }

            commit('setActiveSegment', { segment: activeSegment });

            try {
                if (reFetchAnalytics) {
                    await dispatch('loadVisitorListForSegmentId', {
                        segmentId: segment.id,
                    });
                }
            } catch (error) {
                commit('setActiveSegmentFetching', { fetching: false });
                if (error && error.message !== 'cancelled') {
                    commit('setError', { segmentId: segment.id, error: { message: error.data } });
                }
            }

            commit('setActiveSegmentFetching', { fetching: false });
        },
        loadVisitorListForSegmentId ({ commit, state }, { segmentId }) {
            if (!segmentId) return;

            return runAggregation(visitorList({ segment: state.map[segmentId] })).then((result) => {
                const { eligibleVisitorCount, visitorList } = result[0];
                commit('setActiveSegmentVisitorList', { visitorList });
                commit('setActiveSegmentVisitorCount', {
                    visitorCount: eligibleVisitorCount,
                });
            });
        },
        // please let's get rid of this;
        // used for parsing segment rules
        loadEveryPossibleRuleDependency ({ dispatch, rootGetters }) {
            const canUseEmails = rootGetters['emails/hasEmailStandAlone'];
            const canUseJourneys = rootGetters['journeys/hasJourneysEnabled'];

            const dependencies = [
                dispatch('loadAll'),
                dispatch('apps/loadAll', {}, { root: true }),
                dispatch('metadata/loadAll', {}, { root: true }),
                dispatch('pages/loadAll', {}, { root: true }),
                dispatch('features/loadAll', {}, { root: true }),
                dispatch('guides/loadAll', {}, { root: true }),
                dispatch('trackTypes/loadAll', {}, { root: true }),
                dispatch('workflows/loadAll', {}, { root: true }),
                dispatch('experiments/fetchAllExperiments', {}, { root: true }),
                dispatch('surveys/loadAllItems', {}, { root: true }),
            ];

            if (canUseEmails) {
                dependencies.push(dispatch('emails/loadAll', {}, { root: true }));
            }

            if (canUseJourneys) {
                dependencies.push(dispatch('journeys/loadAllJourneys', {}, { root: true }));
            }

            return Promise.all(dependencies);
        },
        processReplaySegment ({ state, getters, dispatch }, { audience, audienceUiHint } = {}) {
            // if no `audience` has been set yet, `audienceSegment` should be set to the default segment
            let audienceSegment;
            if (!audience || !audienceUiHint) {
                audienceSegment = getDefaultSegment('everyone');

                return audienceSegment;
            }

            // use `audienceUiHint` to identify and set the correct segment from the segments list
            const filters = get(audienceUiHint, 'filters', []);
            const ruleCount = filters.length;
            const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');
            const segmentId = get(filters, '[0].segmentId');
            // identify if custom segment
            const selectedVisitorType = get(findIdentified(audience), 'identified', null);
            let isFilteringByVisitorType;
            const captureReplayForAnon = hasSubscriptionFlag('showAnonymous');

            if (captureReplayForAnon) {
                isFilteringByVisitorType = selectedVisitorType !== null;
            }

            const isExistingSegment = !isCustomNestedSegment && segmentId;
            const isCustomSegment = ruleCount || isFilteringByVisitorType;

            // clear out custom segment to make sure `edit-segment-overlay` doesn't display a previously edited custom
            // segment (ie from guides)
            dispatch('updateCustomSegment', { filters: [], pipeline: [] });

            const segments = getters.list;
            const { defaultSegments, customSegment } = state;

            if (isExistingSegment) {
                audienceSegment = segments.concat(defaultSegments).find((segment) => segment.id === segmentId);
                if (!audienceSegment) {
                    audienceSegment = {
                        id: segmentId,
                        name: 'Unknown Segment',
                    };
                }
            }
            if (!isExistingSegment && isCustomSegment) {
                // use `audience` + `audienceUiHint` to "rebuild" and update the filters/pipeline portion of the
                // custom segment
                const rebuildCustomSegment = { filters, pipeline: audience };
                dispatch('updateCustomSegment', rebuildCustomSegment);
                // avoid holding a reference to custom segment
                audienceSegment = cloneDeep(customSegment);
            }
            if (!isExistingSegment && !isCustomSegment) {
                // we can end up here if the segment is set to a custom segment w/ only "All Visitors" selected
                // (aka Everyone)
                audienceSegment = getDefaultSegment('everyone');
            }

            return audienceSegment;
        },
        async hasGlobalSegmentWithNoAccountRules ({ state, dispatch }, { globalSegment }) {
            if (!globalSegment) {
                return true;
            }

            const id = globalSegment;
            // explicit null check here for global default segments
            if (id === null || isDefaultSegment(id) || id === 'custom') {
                return true;
            }

            await dispatch('loadOne', { id });
            const segment = state.map[id];
            const filters = get(segment, ['definition', 'filters'], []);

            return hasNoAccountRules(filters);
        },
        async getGuideSegmentAggProps ({ state, dispatch }, { guideTargetingSegment, guide }) {
            // we can't trust the `kind` property here if the guideTargetingSegment
            // is an id. it appears to default to "visitor" even when the segment
            // includes account rules.
            if (guideTargetingSegment && isString(guideTargetingSegment) && !isDefaultSegment(guideTargetingSegment)) {
                await dispatch('loadOne', { id: guideTargetingSegment });
                const segment = state.map[guideTargetingSegment];
                const filters = get(segment, ['definition', 'filters'], []);

                return {
                    guideSegmentIdentified: get(findIdentified(segment.pipeline), 'identified', null),
                    hasGuideSegmentWithNoAccountRules: hasNoAccountRules(filters),
                    isNestedSegment: filtersHaveRulesOfKind(filters, 'segment'),
                };
            }

            const filters = get(guide, ['audienceUiHint', 'filters'], []);

            return {
                guideSegmentIdentified: null,
                hasGuideSegmentWithNoAccountRules: hasNoAccountRules(filters),
            };
        },
        async getGuideSegmentVisitorCountAggParams ({ dispatch, rootGetters }, { guide, segmentId = false }) {
            const guideTargetingSegment = getGuideTargetingSegment(guide);
            const guideAudienceIdentified = get(findIdentified(guide.audience), 'identified', null);
            const [guideSegmentAggProps, globalSegmentHasNoAccountRules] = await Promise.all([
                dispatch('getGuideSegmentAggProps', { guideTargetingSegment, guide }),
                dispatch('hasGlobalSegmentWithNoAccountRules', { globalSegment: segmentId }),
            ]);
            const { guideSegmentIdentified, hasGuideSegmentWithNoAccountRules, isNestedSegment } = guideSegmentAggProps;
            const segmentsNeedAccountIds = !(hasGuideSegmentWithNoAccountRules && globalSegmentHasNoAccountRules);

            return {
                guideAudienceIdentified,
                guideSegmentIdentified,
                guideTargetingSegment,
                segmentsNeedAccountIds,
                isMultiApp: rootGetters['apps/isMultiApp'],
                useGlobalSegment: !!segmentId,
                isNestedSegment,
            };
        },
    },
    getters: {
        list: (state) => sortBy(values(state.map), (segment) => get(segment, 'name', '').toLowerCase()),
        segmentById: (state, getters) => (id) => getters.customSegmentById(id) || getters.defaultSegmentById(id),
        defaultSegmentById: (state) => (id) => {
            const found = state.defaultSegments.find((defaultSegment) => {
                return defaultSegment.id === id;
            });

            return found;
        },
        customSegmentById: (state) => (id) => state.map[id],
        areSegmentDependentsLoading: (state) => (segments) => {
            if (!segments || !segments.length) return false;

            return segments.some((segment) => {
                const { id } = segment;
                const segmentDependents = state.segmentDependents[id];

                return !segmentDependents || segmentDependents.fetching;
            });
        },
        countSegmentDependents: (state) => (segment) => {
            if (!segment) return -1;

            const dependents = state.segmentDependents[segment.id];

            if (!dependents || dependents.fetching) return -1;

            const dependentCount = Object.values(dependents).reduce((total, current) => {
                return Array.isArray(current) ? total + current.length : total;
            }, 0);

            return dependentCount;
        },
        selectedSegmentsMap: (state) =>
            state.selectedSegments.reduce((map, segment, index) => {
                map[segment.id] = index;

                return map;
            }, {}),
        parentSegmentsById: (state, getters) => (id) =>
            (id ? getters.list.filter((segment) => segment.compound?.includes(id)) : []),
        segmentIsGuideFriendly: (state, getters) => (segment) =>
            getters.getGuideUnfriendlyRulesForSegment(segment).length <= 0 && !!segment?.shared,
        getGuideUnfriendlyRulesForSegment: (state, getters) => (segment, segmentLookup, featureName) => {
            if (!isEmailSegmentGuideEligible(segment)) {
                const filter = { type: 'email' };

                return [
                    {
                        filter,
                        tooltip: segmentRuleNotSupportedText(filter, featureName),
                    },
                ];
            }

            return (segment?.definition?.filters ?? [])
                .reduce((acc, curr) => (Array.isArray(curr.or) ? [...acc, ...curr.or] : [...acc, curr]), [])
                .filter((filter) => {
                    if (filter.id === 'segment') {
                        const findSegment = segmentLookup || getters.segmentById;

                        return !getters.segmentIsGuideFriendly(findSegment(filter.segmentId));
                    }

                    return !isSupportedGuideFilter(filter);
                })
                .map((filter) => ({ filter, tooltip: segmentRuleNotSupportedText(filter, featureName) }));
        },
        getSegmentGuideEligibility: (state, getters) => (segment, segmentLookup, featureName) => {
            let eligible = true;
            let reason;
            if (!segment.shared) {
                eligible = false;
                reason = $t(
                    'segmentsModule.getSegmentGuideEligibility.reason.This segment is incompatible with {featureName}: visibility set to only me',
                    { featureName: featureName || 'guides' },
                );
            } else {
                const unfriendlyRules = getters.getGuideUnfriendlyRulesForSegment(segment, segmentLookup, featureName);

                if (unfriendlyRules.length > 0) {
                    eligible = false;
                    reason = unfriendlyRules[0].tooltip;
                }
            }

            return { eligible, reason };
        },
        everyPossibleRuleDependency: (state, getters, rootState, rootGetters) => {
            const dependents = {};
            dependents.tags = [
                ...rootGetters['pages/list'],
                ...rootGetters['features/list'],
                ...rootGetters['trackTypes/list'],
                ...rootGetters['guides/list'],
                ...rootGetters['journeys/list'],
                ...rootGetters['emails/list'],
                ...rootGetters['experiments/getAllExperiments'],
                ...rootGetters['surveys/listItems'],
            ];

            dependents.segments = [...getters.list, ...state.defaultSegments];

            dependents.apps = rootGetters['apps/list'];
            dependents.workflows = rootGetters['workflows/list'];
            const types = ['auto', 'agent', 'custom', 'salesforce'];
            dependents.schema = [
                pick(rootState.metadata.map.visitor, types),
                pick(rootState.metadata.map.account, types),
            ];

            return dependents;
        },
        listAdoptUISegments: (state, getters) => {
            return getters.list.filter((segment) => isAdoptSegment(segment));
        },
    },
};

function getSelectedVisitorType (pipeline) {
    return get(findIdentified(pipeline), 'identified', null);
}

export default segments;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
